import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Grid, Card, Toolbar, CardContent, ButtonBase, Dialog, DialogContent, DialogActions, Button, Typography, IconButton, Tooltip } from '@mui/material';
import Image from '../../components/image';
import { DeleteForever as DeleteIcon, AddAPhoto as NoImageIcon } from '@mui/icons-material';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import setFieldData from 'final-form-set-field-data';
import ExtendedField from '../../components/text_field';
import DropZone from '../files/dropzone';
import axios from 'axios';
import Config from '../../config';

// react-final-form's Form/Field generics don't line up with this file's loose
// value shapes; cast to any at the import boundary (same pattern as
// reports.tsx/scorecards.tsx/agreements.tsx/user_profile.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;

const productLimit = 10;

type Product = {
    Id?: number | string;
    Name?: string;
    Description?: string;
    Image?: string;
    [key: string]: any;
};

type ProductProfileState = {
    products: Product[];
    showEdit: boolean;
    noImage: boolean;
    tempImage?: string | null;
    imageFile?: File | null;
    showConfirm: boolean;
    product?: Product;
    confirmContent?: string;
    disableConfirm?: boolean;
    handleConfirm?: () => void;
    disableEdit?: boolean;
};

const ProductProfile = (props: { isRetailer?: boolean; [key: string]: any }) => {
    const [state, setStateRaw] = useState<ProductProfileState>({products: [], showEdit: false, noImage: false, tempImage: null, imageFile: null, showConfirm: false});

    // Mirror the class's always-current `this.state` for reads inside async
    // callbacks; partial updates merge into the previous state to preserve the
    // class `setState(partial)` semantics.
    const stateRef = useRef(state);
    stateRef.current = state;
    const setState = useCallback((partial: Partial<ProductProfileState>) => {
        setStateRaw(prev => ({ ...prev, ...partial }));
    }, []);

    const loadItems = useCallback(async () => {
        var products = (await axios.get(Config.api + "/odata/Company/CompanyProfileProducts")).data.value;
        setState({products});
    }, [setState]);

    useEffect(() => {
        void loadItems();
    }, [loadItems]);

    const readImage = (blob: Blob): Promise<string> => {
        var fileReader = new FileReader();
        return new Promise((resolve, reject) => {
        fileReader.onerror = () => {
          fileReader.abort();
          reject(new Error('Failed to read blob.'));
        };

        fileReader.onload = () => {
          resolve(fileReader.result as string);
        };

        fileReader.readAsDataURL(blob);
      });
    };

    const handleDelete = (item: Product) => {
        setState({
                showConfirm: true,
                confirmContent: `Are you sure you want to delete this product?`,
                disableConfirm: false,
                handleConfirm: async () => {
                    setState({disableConfirm: true});
                    try {
                        await axios.put(Config.api + "/odata/Company/CompanyProfileProducts", {Id: item.Id});
                        await loadItems();
                        setState({showConfirm: false});
                    } catch(e) {
                        setState({showConfirm: false});
                    }
                }
            });
    };

    const edit = (item: Product) => {
        setState({product: item, showEdit: true, disableEdit: false, noImage: !item.Image, tempImage: null, imageFile: null});
    };

    const saveProduct = async (item: Product) => {
        setState({disableEdit: true});
        var response = (await axios.put(Config.api + "/odata/Company/CompanyProfileProducts", item)).data;
        if(stateRef.current.imageFile){
            var formData = new FormData();
            formData.append("file", stateRef.current.imageFile);
            await axios.post(Config.api + '/api/v1/Account/ProductImage?id=' + response.Id, formData, {
                headers: {
                  'Content-Type': 'multipart/form-data'
              }
            });
        }
        await loadItems();
        setState({showEdit: false});
    };

    const onDropImage = async (files: File[]) => {
        if(!files || !files.length){
            return;
        }
        var image = files[0];
        var imageUrl = await readImage(image);
        setState({imageFile: image, tempImage: imageUrl, noImage: false});
    };

    const required = (value: any) => (value ? undefined : 'This field is required.');

    return (
        <Grid container spacing={2}>
            <Grid size={12}>
                <Card style={{textAlign: 'left'}}>
                <Toolbar className='lbtoolbar'>Product Showcase</Toolbar>
                    <CardContent>
                        {state.products.length >= productLimit ? <div>{`You cannot set up more than ${productLimit} products. If you want to add a different product please remove an existing product first.`}</div> : <Button variant='outlined' onClick={() => edit({})}>Add Product</Button>}
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ md: 12 }} style={{textAlign: 'left'}}>
            {state.products.map(item => (
                    <Card key={item.Id} className={'product'} onClick={() => edit(item)}>
                        <ButtonBase>
                            <div>
                                <div className="productImageHolder"><Image className="productImage" src={item.Image || ''} altImage={<Image className="productImage" altImage={<NoImageIcon className="productImage"/>} alt={item.Name ? `${item.Name} image` : ''}/>} alt={item.Name ? `${item.Name} image` : ''}/></div>
                                <div className="productName" dangerouslySetInnerHTML={{__html: item.Name}}></div>
                            </div>
                        </ButtonBase>
                        <Tooltip title='Delete'>
                            <IconButton
                                className='deleteButton'
                                onClick={(e) => {e.stopPropagation(); handleDelete(item)}}
                                size="large"><DeleteIcon/></IconButton>
                        </Tooltip>
                    </Card>
            ))}
            </Grid>
            <Dialog
                maxWidth="sm"
                open={state.showEdit}
                onClose={(event, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (state.disableEdit) return;
                  }
                  setState({showEdit: false});
                }}
                fullWidth={true}>
                {state.product ?
                <Form onSubmit={(item: Product) => saveProduct(item)}
                    initialValues={state.product}
                    mutators={{ setFieldData }}
                    render={({ handleSubmit, pristine, invalid, values, form: {mutators, getState} }: any) => (
                      <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}} autoComplete='off'>
                      <Toolbar className='lbtoolbar'><span dangerouslySetInnerHTML={{__html: state.product.Name || 'Edit Product'}}/></Toolbar>
                    <DialogContent>
                    <div style={{display: 'flex', flexDirection: 'column', flexShrink: '0'}}>
                      <Field style={{marginBottom: '0.2rem'}} validate={required} alwaysShowError={true} disabled={state.disableEdit} label='Name' component={ExtendedField} name='Name'/>
                      <Field style={{marginBottom: '0.2rem'}} alwaysShowError={true} disabled={state.disableEdit} label='Description' component={ExtendedField} name='Description'/>
                      <DropZone accept="image/*" style={{width: '100%', minHeight: '10em', flex: 1, marginTop: '1em'}} onDrop={onDropImage}>
                      {({ isDragAccept, isDragReject, acceptedFiles, rejectedFiles }: any) => {
                          if (isDragAccept || isDragReject) {
                            return "Drop here to upload this file.";
                          }
                          return <div style={{textAlign: 'center'}}>
                                  {state.noImage ? '' : <img alt='Product' style={{marginBottom: '1em', maxWidth: 'calc(100% - 10px)', maxHeight: '200px'}} src={state.tempImage || state.product.Image} onError={() => setState({noImage: true})}/>}
                                  <div>Drop an image file here or click to choose an image.</div>
                                  <div>Images more than 480 pixels tall or wide will be resized.</div>
                              </div>;
                        }}
                      </DropZone>
                      </div>
                    </DialogContent>
                    <DialogActions>
                      <Button type='submit' disabled={(pristine && !state.tempImage) || state.disableEdit || invalid} variant="text" color="primary">Save</Button>
                    </DialogActions>
                    </form>
                    )}/>
                    : ''}
              </Dialog>
            <Dialog
                maxWidth="sm"
                open={state.showConfirm}
                onClose={(event, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (state.disableConfirm) return;
                  }
                  setState({showConfirm: false});
                }}>
                <Toolbar className='lbtoolbar'>Confirm Delete</Toolbar>
                <DialogContent>
                  <Typography>{state.confirmContent}</Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setState({showConfirm: false})} disabled={state.disableConfirm} variant="text" color="primary">
                    No
                  </Button>
                  <Button onClick={state.handleConfirm} disabled={state.disableConfirm} variant="text" color="primary">
                    Yes
                  </Button>
                </DialogActions>
              </Dialog>
        </Grid>
    );
};

export default ProductProfile;
